import React, { useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Book } from '../../../types';
import Book3D from '../../common/Book3D';
import {
    getImagePaths,
    getBestImagePath,
    shouldUseThumbnail
} from '../../../utils/imageUtils';
import { prefersReducedMotion } from '../../../utils/noise';
import { launchBook } from '../../common/bookTransition';
import './BookCatalogue.css';

/** How far the books tip open as they pass the middle of the screen. */
const TILT_FLAT = 84;
const TILT_OPEN = 68;

const BookCatalogue: React.FC<{ books: Book[] }> = ({ books }) => {
    const rootRef = useRef<HTMLOListElement>(null);
    const navigate = useNavigate();

    /* The stack tips as you scroll: a book lies nearly shut at the edges of
       the screen and opens toward you as it crosses the middle, so you see
       a sliver of its cover. That is the whole interaction, so it has to be
       cheap — one passive listener for the page, coalesced into a single
       rAF, and nothing computed for books that are off-screen. */
    useEffect(() => {
        const root = rootRef.current;
        if (!root || prefersReducedMotion()) return;

        /* The angle is written onto the row, not onto the book. An inline
           style on the book itself would outrank every stylesheet rule
           below it, so hovering a book — or standing one up — could never
           override the angle scrolling had just given it. Set one level up
           and the book's own rules win, as they should. */
        const rows = Array.from(
            root.querySelectorAll<HTMLElement>('.book-catalogue__row')
        );
        let queued = false;

        const paint = () => {
            queued = false;
            const viewport = window.innerHeight;
            const middle = viewport / 2;

            for (const row of rows) {
                const rect = row.getBoundingClientRect();
                if (rect.bottom < -0.25 * viewport || rect.top > 1.25 * viewport) continue;

                const centre = rect.top + rect.height / 2;
                const distance = Math.min(1, Math.abs(centre - middle) / middle);
                const tilt = TILT_OPEN + (TILT_FLAT - TILT_OPEN) * distance;

                row.style.setProperty('--book-tilt', `${tilt}deg`);
            }
        };

        const onScroll = () => {
            if (queued) return;
            queued = true;
            requestAnimationFrame(paint);
        };

        paint();
        window.addEventListener('scroll', onScroll, { passive: true });
        window.addEventListener('resize', onScroll);

        return () => {
            window.removeEventListener('scroll', onScroll);
            window.removeEventListener('resize', onScroll);
        };
    }, [books]);

    /* Picking a book hands its position and pose to the book page, which
       stands it up on arrival. Doing the rise here and then navigating
       played two animations back to back, with a jump between them where
       the page changed — one movement, owned by the page being arrived at,
       is the whole point.

       The anchor underneath is a real link: modified clicks are left alone
       so opening in a new tab still works, and without motion the
       navigation is an ordinary one. */
    const select = (event: React.MouseEvent<HTMLAnchorElement>, id: string) => {
        const object = event.currentTarget.querySelector<HTMLElement>('.book3d');

        if (
            !object ||
            event.defaultPrevented ||
            event.button !== 0 ||
            event.metaKey ||
            event.ctrlKey ||
            event.shiftKey ||
            event.altKey ||
            prefersReducedMotion()
        ) {
            return;
        }

        const rect = object.getBoundingClientRect();
        const tilt =
            getComputedStyle(object).getPropertyValue('--book-tilt').trim() ||
            `${TILT_FLAT}deg`;

        event.preventDefault();
        launchBook({
            id,
            top: rect.top,
            left: rect.left,
            /* Written out as the same list of functions the arrival ends on,
               so the two interpolate axis by axis. Handing over a computed
               matrix instead would leave the browser to decompose it and
               pick its own path through the rotation. */
            transform: `rotateX(${tilt}) rotateY(0deg) rotateZ(-90deg)`
        });
        navigate(`/books/${id}`);
    };

    return (
        <ol className="book-catalogue" ref={rootRef}>
            {books.map(book => {
                const cover = getBestImagePath(
                    getImagePaths(book.coverImage || ''),
                    shouldUseThumbnail('grid')
                );

                return (
                    <li key={book.id} className="book-catalogue__row">
                        <Link
                            to={`/books/${book.id}`}
                            className="book-catalogue__link"
                            onClick={event => select(event, book.id)}
                        >
                            <Book3D cover={cover} title={book.title} author={book.author} />
                            {/* The spine carries the lettering; this is the
                                same text for anyone not looking at pixels. */}
                            <span className="book-catalogue__label">
                                {book.author} — {book.title}, {book.publishYear}
                            </span>
                        </Link>
                    </li>
                );
            })}
        </ol>
    );
};

export default BookCatalogue;
